import { saveAs } from 'file-saver';
import Handlebars from 'handlebars/runtime';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';

import medicoCXCommercialTemplate from '@/templates/sob/medicoCXcommercial.html';
// Import templates
import medicoCXMedicareTemplate from '@/templates/sob/medicoCXmedicare.html';
import type { CompressionProgress, PDFFile } from '@/workers/sob.zip.worker';

import { sobWorkerService } from './sobWorkerService';

// Programs eligible for SOB generation (program ID -> precompiled template mapping)
const SOB_ENABLED_PROGRAMS: Record<string, Handlebars.TemplateDelegate> = {
  // Medicare programs - use Medicare template
  '68ec2860d6f3c60305ae23a9': medicoCXMedicareTemplate,
  '68ee6efe278a641b067dcba8': medicoCXMedicareTemplate,
  '690c7dd3cc16796003707a2b': medicoCXMedicareTemplate,
  '68f2ab1dc6b5c94af5f5ad05': medicoCXMedicareTemplate,

  '68be9678d1e55d74ec776b4c': medicoCXCommercialTemplate,
  '68b5b37eedf7fd744bf89357': medicoCXCommercialTemplate,
  '68b71c4078875b3c65daab18': medicoCXCommercialTemplate,
  '68be9b82c83aac386b56e7ea': medicoCXCommercialTemplate,
  '68b985240bf8b2ab77f59463': medicoCXCommercialTemplate,
  '68b976bd0bf8b2ab77f59454': medicoCXCommercialTemplate,

  // Add more program-specific templates here as needed
  // Note: Only programs listed here will have SOB features enabled
};

// Register Handlebars helper for conditional checkboxes
Handlebars.registerHelper('CHK_EQ', function (field: any, value: any) {
  return field === value;
});

// Register Handlebars helper for case-insensitive equality check
Handlebars.registerHelper('CHK_EQ_CI', function (field: any, value: any) {
  if (!field || !value) {
    return false;
  }
  return String(field).toLowerCase() === String(value).toLowerCase();
});

// Register Handlebars helper for safe string output
Handlebars.registerHelper('safe', function (value: any) {
  return value || '';
});

// Helper function to determine form type from a string
function determineFormType(value: string): string | null {
  const formNorm = value.toLowerCase();

  // Check for SDV/single dose vial first (should map to Vial)
  if (
    /\bsdv\b/.test(formNorm) ||
    /\bsingle\s*dose\s*vial\b/.test(formNorm) ||
    /\bsingle[-\s]*dose\b/.test(formNorm)
  ) {
    return 'Vial';
  }

  // Check for pre-filled syringe
  if (
    /\bpfs\b/.test(formNorm) ||
    /pre[-\s]*filled/.test(formNorm) ||
    /\bsyringe\b/.test(formNorm)
  ) {
    return 'Pre-filled Syringe';
  }

  // Check for vial
  if (/\bvial\b/.test(formNorm)) {
    return 'Vial';
  }

  // Check for auto-injector (auto followed by injector)
  if (/\bauto\b.*\binjector\b/.test(formNorm)) {
    return 'Auto-injector';
  }

  return null;
}

// Register Handlebars helper to determine form from formulation
Handlebars.registerHelper('FORM_FROM_FORMULATION', function (formulation: any, existingForm: any) {
  // Check formulation field first
  if (formulation) {
    const result = determineFormType(String(formulation));
    if (result) {
      return result;
    }
  }

  // Check form field as fallback
  if (existingForm) {
    const result = determineFormType(String(existingForm));
    if (result) {
      return result;
    }
    // Return the original form value if no patterns match
    return existingForm;
  }

  return '';
});

// Register Handlebars helper to generate notes from primary eligibility status
Handlebars.registerHelper(
  'NOTES_FROM_ELIGIBILITY',
  function (eligibilityStatus: any, existingNotes: any) {
    if (eligibilityStatus) {
      const mpsNorm = String(eligibilityStatus).toLowerCase();
      // Check "Inactive" first so it doesn't get caught by "active" substring cases
      if (/\binactive\b/.test(mpsNorm)) {
        return "Patient doesn't have an active Medicare Part B coverage";
      } else if (/\bactive coverage\b/.test(mpsNorm) || mpsNorm === 'active coverage') {
        return 'Patient has an active Medicare Part B coverage and Plan runs on a calendar year.';
      }
    }
    // Return existing notes if no match
    return existingNotes || '';
  },
);

// Register Handlebars helper to generate supplement notes based on plan letter
Handlebars.registerHelper('SUPPLEMENT_NOTES_FROM_PLAN', function (planLetter: any) {
  if (!planLetter) {
    return '';
  }

  const letter = String(planLetter).toUpperCase().trim();

  // Scenario 1 - Plans A, B, D, G, M, N
  if (['A', 'B', 'D', 'G', 'M', 'N'].includes(letter)) {
    return 'This plan is a supplement to Medicare Part B and covers 100% of the remaining coinsurance. The patient is responsible for the Part B deductible.';
  }
  // Scenario 2 - Plans C, F
  if (['C', 'F'].includes(letter)) {
    return 'This plan is a supplement to Medicare Part B and covers 100% of the remaining coinsurance and deductible.';
  }
  // Scenario 3 - Plan K
  if (letter === 'K') {
    return 'This plan is a supplement to Medicare Part B and covers 50% of the remaining coinsurance only. After the patient meets the OOP limit of $8000 (in 2026), plan covers 100%. The patient is responsible for the Part B deductible.';
  }
  // Scenario 4 - Plan L
  if (letter === 'L') {
    return 'This plan is a supplement to Medicare Part B and covers 75% of the remaining coinsurance only. After the patient meets the OOP limit of $4000 (in 2026), plan covers 100%. The patient is responsible for the Part B deductible.';
  }

  // No matching condition - return empty string
  return '';
});

// Register Handlebars helper to check if a field contains certain keywords
Handlebars.registerHelper('CHK_HAS', function (field: any, keywords: any) {
  if (!field || !keywords) {
    return false;
  }

  const fieldStr = String(field).toLowerCase();
  const keywordList = String(keywords).toLowerCase().split('|');

  return keywordList.some(keyword => fieldStr.includes(keyword));
});

// Returns the first non-empty value from the arguments (like SQL COALESCE)
// Useful for backward compatibility when a field was renamed or a new field introduced
Handlebars.registerHelper('COALESCE', function (...args: any[]): string {
  // Remove the Handlebars options object (last argument)
  const values = args.slice(0, -1);

  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return String(value);
    }
  }
  return '';
});

/**
 * Check if a program is eligible for SOB generation
 * @param programId - The program ID
 * @returns True if the program supports SOB features
 */
export const isProgramSOBEnabled = (programId: string): boolean => {
  return programId in SOB_ENABLED_PROGRAMS;
};

/**
 * Get precompiled SOB template for a specific program
 * @param programId - The program ID
 * @returns The precompiled Handlebars template or null if not eligible
 */
const getSOBTemplate = (programId: string): Handlebars.TemplateDelegate | null => {
  return SOB_ENABLED_PROGRAMS[programId] || null;
};

interface SOBData {
  input: Record<string, any>;
  output: Record<string, any>;
  metadata?: Record<string, any>;
  case_id: string;
  reference_id?: string;
  summary?: {
    section_summaries?: Record<string, string>;
  };
}

/**
 * Extracts nested data from case record, handling both wrapped and unwrapped formats
 */
const extractCaseData = (data: any): Record<string, any> => {
  if (!data) return {};
  return data.data ?? data;
};

/**
 * Helper function to prepare template data from case data
 */
const prepareTemplateData = (
  caseData: any,
  summary?: { section_summaries?: Record<string, string> },
): SOBData => {
  // Extract input and output data with proper field mappings
  const inputData = extractCaseData(caseData.input_data);
  const outputData = extractCaseData(caseData.output_data);

  // Add default values for output fields if not present
  // Use workflow_completed_at if available, otherwise fall back to current date
  const dateCompleted = caseData.workflow_completed_at
    ? new Date(caseData.workflow_completed_at).toLocaleDateString('en-US')
    : new Date().toLocaleDateString('en-US');

  const defaultOutputValues = {
    representative_name: 'Automated System',
    benefits_completed_by: 'Souvik Choudhury',
    date_completed: dateCompleted,
    ...outputData,
  };

  // Prepare data for template
  return {
    input: inputData,
    output: defaultOutputValues,
    metadata: caseData.metadata ?? {},
    case_id: caseData.id ?? '',
    reference_id: caseData.reference_id ?? '',
    summary: summary ?? {},
  };
};

export const sobService = {
  /**
   * Generate HTML from template and case data
   */
  generateSOBHTML: (
    caseData: any,
    programId: string,
    summary?: { section_summaries?: Record<string, string> },
  ): string => {
    const template = getSOBTemplate(programId);
    if (!template) {
      throw new Error(`SOB generation is not available for program ${programId}`);
    }
    const templateData = prepareTemplateData(caseData, summary);
    return template(templateData);
  },

  /**
   * Convert HTML to PDF with multi-page support
   */
  htmlToPDF: async (html: string, fileName: string): Promise<void> => {
    // Create a container for the HTML
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '210mm'; // A4 width
    document.body.appendChild(container);

    try {
      // Wait for any images to load
      await new Promise(resolve => setTimeout(resolve, 100));

      // Create PDF with compression
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

      // Define margins (in mm)
      const topMargin = 25; // More space on top
      const bottomMargin = 25; // More space on bottom
      const leftMargin = 15; // Less space on left
      const rightMargin = 15; // Less space on right

      // Calculate available space
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const availableWidth = pageWidth - leftMargin - rightMargin;
      const availableHeight = pageHeight - topMargin - bottomMargin;

      // Find all pdf-page sections
      const pageElements = container.querySelectorAll('.pdf-page');

      if (pageElements.length > 0) {
        // Process each page section separately
        for (let i = 0; i < pageElements.length; i++) {
          const pageElement = pageElements[i] as HTMLElement;

          // Convert this page section to canvas
          const canvas = await html2canvas(pageElement, {
            scale: 2, // Higher quality
            useCORS: true,
            logging: false,
            windowWidth: 794, // A4 width in pixels at 96 DPI
          });

          // Calculate scaled dimensions to fit within margins
          const imgData = canvas.toDataURL('image/png');
          const imgAspectRatio = canvas.width / canvas.height;

          let finalWidth = availableWidth;
          let finalHeight = availableWidth / imgAspectRatio;

          // If height exceeds available height, scale down
          if (finalHeight > availableHeight) {
            finalHeight = availableHeight;
            finalWidth = availableHeight * imgAspectRatio;
          }

          if (i > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, 'PNG', leftMargin, topMargin, finalWidth, finalHeight);
        }
      } else {
        // Fallback to original behavior if no pdf-page sections found
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: 794,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
      }

      // Save the PDF
      pdf.save(fileName);
    } finally {
      // Clean up
      document.body.removeChild(container);
    }
  },

  /**
   * Convert HTML to PDF Blob (for bulk export)
   */
  htmlToPDFBlob: async (html: string): Promise<Blob> => {
    // Create a container for the HTML
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '210mm'; // A4 width
    document.body.appendChild(container);

    try {
      // Wait for any images to load
      await new Promise(resolve => setTimeout(resolve, 100));

      // Create PDF with compression
      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

      // Define margins (in mm)
      const topMargin = 25; // More space on top
      const bottomMargin = 25; // More space on bottom
      const leftMargin = 15; // Less space on left
      const rightMargin = 15; // Less space on right

      // Calculate available space
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const availableWidth = pageWidth - leftMargin - rightMargin;
      const availableHeight = pageHeight - topMargin - bottomMargin;

      // Find all pdf-page sections
      const pageElements = container.querySelectorAll('.pdf-page');

      if (pageElements.length > 0) {
        // Process each page section separately
        for (let i = 0; i < pageElements.length; i++) {
          const pageElement = pageElements[i] as HTMLElement;

          // Convert this page section to canvas
          const canvas = await html2canvas(pageElement, {
            scale: 2, // Higher quality
            useCORS: true,
            logging: false,
            windowWidth: 794, // A4 width in pixels at 96 DPI
          });

          // Calculate scaled dimensions to fit within margins
          const imgData = canvas.toDataURL('image/png');
          const imgAspectRatio = canvas.width / canvas.height;

          let finalWidth = availableWidth;
          let finalHeight = availableWidth / imgAspectRatio;

          // If height exceeds available height, scale down
          if (finalHeight > availableHeight) {
            finalHeight = availableHeight;
            finalWidth = availableHeight * imgAspectRatio;
          }

          if (i > 0) {
            pdf.addPage();
          }

          pdf.addImage(imgData, 'PNG', leftMargin, topMargin, finalWidth, finalHeight);
        }
      } else {
        // Fallback to original behavior if no pdf-page sections found
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          logging: false,
          windowWidth: 794,
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
      }

      // Return as blob
      return pdf.output('blob');
    } finally {
      // Clean up
      document.body.removeChild(container);
    }
  },

  /**
   * Download single SOB as PDF
   */
  downloadSOB: async (
    caseData: any,
    programId: string,
    summary?: { section_summaries?: Record<string, string> },
  ): Promise<void> => {
    const html = sobService.generateSOBHTML(caseData, programId, summary);
    const fileName = `${programId}-${caseData.id}.pdf`;
    await sobService.htmlToPDF(html, fileName);
  },

  /**
   * Download multiple SOBs as ZIP (legacy method - synchronous compression)
   * Each case in casesData can have an optional `summary` property with section_summaries
   */
  downloadBulkSOB: async (
    casesData: any[],
    programId: string,
    programName: string,
    onProgress?: (current: number, total: number) => void,
  ): Promise<void> => {
    const zip = new JSZip();
    const total = casesData.length;

    for (let i = 0; i < casesData.length; i++) {
      const caseData = casesData[i];

      // Update progress
      if (onProgress) {
        onProgress(i + 1, total);
      }

      // Generate HTML and convert to PDF blob (include summary if available)
      const html = sobService.generateSOBHTML(caseData, programId, caseData.summary);
      const pdfBlob = await sobService.htmlToPDFBlob(html);

      // Add PDF to zip with unique filename
      const fileName = `${programId}-${caseData.id}.pdf`;
      zip.file(fileName, pdfBlob);
    }

    // Generate and download zip
    const zipBlob = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6,
      },
    });

    const date = new Date().toISOString().split('T')[0];
    const zipFileName = `sob-${programName.toLowerCase().replace(/\s+/g, '-')}-${date}.zip`;
    saveAs(zipBlob, zipFileName);
  },

  /**
   * Download multiple SOBs as ZIP using Web Worker for compression
   * Each case in casesData can have an optional `summary` property with section_summaries
   */
  downloadBulkSOBWithWorker: async (
    casesData: any[],
    programId: string,
    programName: string,
    onProgress?: (current: number, total: number, phase: 'generating' | 'compressing') => void,
  ): Promise<void> => {
    const total = casesData.length;
    const pdfFiles: PDFFile[] = [];

    // Phase 1: Generate all PDFs on main thread (needs DOM access)
    for (let i = 0; i < casesData.length; i++) {
      const caseData = casesData[i];

      // Update progress for PDF generation
      if (onProgress) {
        onProgress(i + 1, total, 'generating');
      }

      // Generate HTML and convert to PDF blob (include summary if available)
      const html = sobService.generateSOBHTML(caseData, programId, caseData.summary);
      const pdfBlob = await sobService.htmlToPDFBlob(html);

      // Collect PDF data for worker
      const fileName = `${programId}-${caseData.id}.pdf`;
      pdfFiles.push({
        fileName,
        blob: pdfBlob,
      });
    }

    // Phase 2: Compress PDFs to ZIP using worker (off main thread)
    const date = new Date().toISOString().split('T')[0];
    const zipFileName = `sob-${programName.toLowerCase().replace(/\s+/g, '-')}-${date}.zip`;

    // Initialize worker if not already done
    if (!sobWorkerService.isInitialized()) {
      await sobWorkerService.initialize();
    }

    // Compress using worker with progress callback
    const zipBlob = await sobWorkerService.compressPDFs(
      pdfFiles,
      (progress: CompressionProgress) => {
        if (onProgress) {
          onProgress(
            progress.current,
            progress.total,
            progress.phase === 'adding' || progress.phase === 'compressing'
              ? 'compressing'
              : 'generating',
          );
        }
      },
    );

    // Download the ZIP file
    saveAs(zipBlob, zipFileName);
  },
};
